import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { GPXFile } from "PosterEditor/model/poster-types";

export const useUploadsStore = () => {
  const { userID, client: supabase } = useAuthContext();

  const imageURLsInUploadsFolder = async () => {
    if (!userID) {
      // User is not always authenticated at this stage so don't throw.
      return [];
    }
    if (!supabase) throw new Error("Supabase client not initialized");

    const folderPath = `images/${userID}/uploads/`;

    const { data: files, error } = await supabase.storage
      .from("user-images")
      .list(folderPath, { limit: 100 });

    if (error) throw error;

    const jpgFiles = files?.filter((file) => file.name.endsWith(".jpeg")) ?? [];

    const signedURLs = await Promise.all(
      jpgFiles.map(async (file) => {
        const { data, error: urlError } = await supabase.storage
          .from("user-images")
          .createSignedUrl(`${folderPath}${file.name}`, 60 * 60 * 24);
        if (urlError || !data) return null;
        return data.signedUrl;
      })
    );
    return signedURLs.filter((url): url is string => url !== null);
  };

  const deleteImageWithURL = async (url: string) => {
    if (!userID) throw new Error("deleteImageWithURL: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");
    if (!url) throw new Error("deleteImageWithURL: URL is required");

    // Parse the URL to extract the path after /images/
    let filePath: string | null = null;
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname; // e.g. /storage/v1/object/public/user-images/images/userid/uploads/filename.jpeg
      const imagesIndex = pathname.indexOf(`/images/${userID}/uploads/`);
      if (imagesIndex === -1) {
        throw new Error(
          "deleteImageWithURL: URL does not contain expected images path"
        );
      }
      filePath = pathname.substring(imagesIndex + 1); // remove leading slash
    } catch (e) {
      throw new Error("deleteImageWithURL: Invalid URL format");
    }

    const { error } = await supabase.storage
      .from("user-images")
      .remove([filePath]);
    if (error) throw error;
    return true;
  };

  const deleteImagesWithURLs = async (urls: string[]) => {
    if (!userID)
      throw new Error("deleteImagesWithURLs: User not authenticated");
    if (!supabase) throw new Error("Supabase client not initialized");
    if (!urls || urls.length === 0)
      throw new Error("deleteImagesWithURLs: URLs array is required");

    const filePaths: string[] = urls.map((url) => {
      try {
        const parsedUrl = new URL(url);
        const pathname = parsedUrl.pathname;
        const imagesIndex = pathname.indexOf(`/images/${userID}/uploads/`);
        if (imagesIndex === -1) {
          throw new Error(
            "deleteImagesWithURLs: URL does not contain expected images path"
          );
        }
        return pathname.substring(imagesIndex + 1); // remove leading slash
      } catch {
        throw new Error("deleteImagesWithURLs: Invalid URL format");
      }
    });

    const { error } = await supabase.storage
      .from("user-images")
      .remove(filePaths);
    if (error) throw error;
    return true;
  };

  const gpxURLsInUploadsFolder = async () => {
    if (!userID) {
      // User is not always authenticated at this stage so don't throw.
      return [];
    }
    if (!supabase) throw new Error("Supabase client not initialized");

    const folderPath = `images/${userID}/uploads/gpx/`;

    const { data: files, error } = await supabase.storage
      .from("user-images")
      .list(folderPath, { limit: 100 });

    if (error) throw error;

    const gpxFiles = files?.filter((file) => file.name.endsWith(".gpx")) ?? [];

    const signedURLs = await Promise.all(
      gpxFiles.map(async (file) => {
        const { data, error: urlError } = await supabase.storage
          .from("user-images")
          .createSignedUrl(`${folderPath}${file.name}`, 60 * 60 * 24);
        if (urlError || !data) return null;
        return data.signedUrl;
      })
    );
    return signedURLs.filter((url): url is string => url !== null);
  };

  const deleteGPXWithURL = async (url: string) => {
    if (!userID) throw new Error("deleteGPXWithURL: User not authenticated");
    if (!supabase)
      throw new Error("deleteGPXWithURL: Supabase client not initialized");

    const { filename } = extractFilenameAndExtension(url);
    const folderPath = `images/${userID}/uploads/gpx/`;

    const { error } = await supabase.storage
      .from("user-images")
      .remove([`${folderPath}${filename}.gpx`]);
    if (error) throw error;

    return true;
  };

  const extractFilenameAndExtension = (
    url: string
  ): { filename: string; extension: string } => {
    try {
      const parsedUrl = new URL(url);
      const pathname = parsedUrl.pathname; // e.g. /storage/v1/object/public/user-images/images/userid/uploads/filename.jpeg
      const parts = pathname.split("/");
      const file = parts[parts.length - 1]; // "filename.jpeg"

      const dotIndex = file.lastIndexOf(".");
      if (dotIndex === -1) throw new Error("No extension found in filename");

      const filename = file.substring(0, dotIndex);
      const extension = file.substring(dotIndex + 1);
      return { filename, extension };
    } catch (err) {
      throw new Error(`Invalid URL: ${url}`);
    }
  };

  return {
    imageURLsInUploadsFolder,
    gpxURLsInUploadsFolder,
    deleteGPXWithURL,
    deleteImageWithURL,
    deleteImagesWithURLs,
  };
};
